import React, { useEffect } from 'react';
import { Tablet, Wifi, WifiOff, PenLine } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { PairedCourt, PairingLink } from '../../hooks/useCourtPairing';
import { useWakeLock } from '../../hooks/useWakeLock';

interface PairedIdleScreenProps {
  pairedCourt: PairedCourt;
  link: PairingLink;
  wakeLock: ReturnType<typeof useWakeLock>;
  /** "Enter a match manually" on the paired court; no button when absent (config `paired.allowManualMatch`). */
  onManualMatch?: () => void;
}

/**
 * Idle screen of a tablet paired with a court: waits for the match the tournament system
 * sends to that court. May offer a manual match on the same court (off by default). Unpairing is the center
 * manager's call (revoke-device.php), never the referee's: the tablet offers none.
 */
export function PairedIdleScreen({ pairedCourt, link, wakeLock, onManualMatch }: PairedIdleScreenProps) {
  const { t } = useTranslation();
  const { court, label } = pairedCourt;

  // Nobody touches the tablet between two matches: keep its screen on while it waits
  const { isSupported, request, release } = wakeLock;
  useEffect(() => {
    if (!isSupported) return;
    request();
    return () => { release(); };
  }, [isSupported, request, release]);

  const linkStyle = link === 'offline'
    ? { icon: WifiOff, className: 'text-red-600', text: t('screens.pairing.link.offline') }
    : link === 'online'
      ? { icon: Wifi, className: 'text-green-600', text: t('screens.pairing.link.online') }
      : { icon: Wifi, className: 'text-gray-400', text: t('screens.pairing.link.idle') };
  const LinkIcon = linkStyle.icon;

  return (
    <div className="min-h-screen p-4 flex items-center justify-center bg-gray-50" data-testid="paired-idle-screen">
      <div className="w-full max-w-md">
        <div className="bg-white p-6 text-center mb-4">
          <div className="flex justify-center mb-4">
            <div className="bg-blue-100 p-3">
              <Tablet className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{court.centerName}</p>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">{court.courtName}</h1>
          <p className="text-sm text-gray-500">{t('screens.pairing.device')}: {label}</p>
        </div>

        <div className="bg-white p-6 text-center mb-4">
          <div className="flex items-center justify-center gap-3 mb-2">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
            </span>
            <span className="font-semibold text-gray-900">{t('screens.pairing.waiting')}</span>
          </div>
          <p className="text-sm text-gray-500 mb-4">{t('screens.pairing.waitingHint')}</p>
          <p className={`inline-flex items-center gap-2 text-xs ${linkStyle.className}`} data-testid="pairing-link">
            <LinkIcon className="w-4 h-4" />
            {linkStyle.text}
          </p>
        </div>

        {onManualMatch && (
          <div className="bg-white p-4">
            <button
              type="button"
              onClick={onManualMatch}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-white font-semibold hover:opacity-90"
              style={{ backgroundColor: '#4E4C4B' }}
            >
              <PenLine className="w-5 h-5" />
              {t('screens.pairing.manualMatch')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
